import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCorners,
  DragStartEvent, DragOverEvent, DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  Project, ProjectStatus, Task, TaskPriority, TaskStatus,
  CreateTaskRequest, UpdateTaskRequest,
  Repository, CreateRepositoryRequest, UpdateRepositoryRequest,
} from '../types';
import projectService from '../services/projectService';
import taskService from '../services/taskService';
import repositoryService from '../services/repositoryService';
import { usePageTitle } from '../hooks/usePageTitle';
import { Badge, ModalOverlay } from '../components/ui';

/* ============================================================
   Labels & visuals
   ============================================================ */

const statusVariant = (s: ProjectStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case ProjectStatus.ACTIVE: return 'green';
    case ProjectStatus.PAUSED: return 'amber';
    case ProjectStatus.COMPLETED: return 'purple';
    case ProjectStatus.ARCHIVED: return 'red';
  }
};
const statusLabel = (s: ProjectStatus): string => {
  switch (s) {
    case ProjectStatus.ACTIVE: return 'Aktiv';
    case ProjectStatus.PAUSED: return 'Pausiert';
    case ProjectStatus.COMPLETED: return 'Abgeschlossen';
    case ProjectStatus.ARCHIVED: return 'Archiviert';
  }
};

const COLUMNS: { id: TaskStatus; label: string; hint: string }[] = [
  { id: TaskStatus.TODO,        label: 'ToDo',           hint: 'Geplant' },
  { id: TaskStatus.IN_PROGRESS, label: 'In Bearbeitung', hint: 'Läuft' },
  { id: TaskStatus.DONE,        label: 'Done',           hint: 'Erledigt' },
];

const priorityLabel = (p: TaskPriority): string =>
  p === TaskPriority.HIGH ? 'Hoch' : p === TaskPriority.MEDIUM ? 'Mittel' : 'Niedrig';

const priorityAccent = (p: TaskPriority): string => {
  switch (p) {
    case TaskPriority.HIGH:   return 'var(--badge-red-text)';
    case TaskPriority.MEDIUM: return 'var(--badge-amber-text)';
    case TaskPriority.LOW:    return 'var(--text-muted)';
  }
};

const priorityChipStyle = (p: TaskPriority): React.CSSProperties => {
  switch (p) {
    case TaskPriority.HIGH:
      return { background: 'var(--badge-red-bg)',   color: 'var(--badge-red-text)' };
    case TaskPriority.MEDIUM:
      return { background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-text)' };
    case TaskPriority.LOW:
      return { background: 'var(--table-header-bg)', color: 'var(--text-muted)' };
  }
};

const toHref = (d: string) => /^https?:\/\//i.test(d) ? d : `https://${d}`;
const prettyDomain = (d: string) => d.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

/* ============================================================
   Icons
   ============================================================ */

const I = {
  Plus: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16M4 12h16" />
    </svg>
  ),
  Back: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Edit: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  Trash: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    </svg>
  ),
  Globe: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </svg>
  ),
  Branch: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="6" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="9" r="2" />
      <path strokeLinecap="round" d="M6 8v8M18 11c0 5-6 4-6 7" />
    </svg>
  ),
  Link: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1 1M14 10a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1-1" />
    </svg>
  ),
  External: () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h5" />
    </svg>
  ),
  Drag: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="6" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="15" cy="18" r="1.5" />
    </svg>
  ),
};

/* ============================================================
   Editor types
   ============================================================ */

type TaskForm = {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  tags: string[];
};

type RepoForm = {
  name: string;
  gitUrl: string;
  defaultBranch: string;
  description: string;
  active: boolean;
};

type Editor =
  | { kind: 'task'; mode: 'create'; data: TaskForm }
  | { kind: 'task'; mode: 'edit'; id: number; data: TaskForm }
  | { kind: 'repo'; mode: 'create'; data: RepoForm }
  | { kind: 'repo'; mode: 'edit'; id: number; data: RepoForm };

type Confirm =
  | { kind: 'task'; id: number; title: string }
  | { kind: 'repo'; id: number; name: string };

const emptyTask = (status: TaskStatus): TaskForm => ({
  title: '', description: '', status, priority: TaskPriority.MEDIUM, tags: [],
});

const emptyRepo: RepoForm = {
  name: '', gitUrl: '', defaultBranch: 'main', description: '', active: true,
};

/* ============================================================
   Main page
   ============================================================ */

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? Number(id) : NaN;
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [repos, setRepos] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  usePageTitle(project?.name ?? 'Projekt');

  useEffect(() => {
    if (!Number.isFinite(projectId)) {
      navigate('/workspace', { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const editorOpen = editor !== null;
  useEffect(() => {
    if (editorOpen && modalRef.current) {
      modalRef.current.querySelector<HTMLInputElement>('input, select, textarea')?.focus();
    }
  }, [editorOpen]);

  const load = async () => {
    try {
      setLoading(true);
      const [p, ts, rs] = await Promise.all([
        projectService.getById(projectId),
        taskService.listByProject(projectId),
        repositoryService.getAll(projectId),
      ]);
      setProject(p);
      setTasks(ts);
      setRepos(rs);
    } catch (err: any) {
      toast.error('Konnte Projekt nicht laden');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- columns ---------- */

  const tasksByColumn = useMemo(() => {
    const map: Record<TaskStatus, Task[]> = {
      [TaskStatus.TODO]: [],
      [TaskStatus.IN_PROGRESS]: [],
      [TaskStatus.DONE]: [],
    };
    for (const t of tasks) map[t.status].push(t);
    for (const s of Object.values(TaskStatus)) {
      map[s].sort((a, b) => a.position - b.position);
    }
    return map;
  }, [tasks]);

  /* ---------- task CRUD ---------- */

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editor) return;
    try {
      if (editor.kind === 'task') {
        if (editor.mode === 'create') {
          const payload: CreateTaskRequest = {
            title: editor.data.title,
            description: editor.data.description || null,
            status: editor.data.status,
            priority: editor.data.priority,
            tags: editor.data.tags,
          };
          const created = await taskService.create(projectId, payload);
          setTasks((prev) => [...prev, created]);
          toast.success('Aufgabe angelegt');
        } else {
          const payload: UpdateTaskRequest = {
            title: editor.data.title,
            description: editor.data.description || null,
            status: editor.data.status,
            priority: editor.data.priority,
            tags: editor.data.tags,
          };
          const updated = await taskService.update(editor.id, payload);
          setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
          toast.success('Aufgabe aktualisiert');
        }
      } else {
        if (editor.mode === 'create') {
          const payload: CreateRepositoryRequest = {
            projectId,
            name: editor.data.name,
            gitUrl: editor.data.gitUrl,
            defaultBranch: editor.data.defaultBranch || 'main',
            description: editor.data.description || null,
          };
          const created = await repositoryService.create(payload);
          setRepos((prev) => [...prev, created]);
          toast.success('Repository angelegt');
        } else {
          const payload: UpdateRepositoryRequest = {
            projectId,
            name: editor.data.name,
            gitUrl: editor.data.gitUrl,
            defaultBranch: editor.data.defaultBranch || 'main',
            description: editor.data.description || null,
            active: editor.data.active,
          };
          const updated = await repositoryService.update(editor.id, payload);
          setRepos((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
          toast.success('Repository aktualisiert');
        }
      }
      setEditor(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Speichern fehlgeschlagen');
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.kind === 'task') {
        await taskService.delete(confirm.id);
        setTasks((prev) => prev.filter((t) => t.id !== confirm.id));
        toast.success('Aufgabe gelöscht');
      } else {
        await repositoryService.delete(confirm.id);
        setRepos((prev) => prev.filter((r) => r.id !== confirm.id));
        toast.success('Repository gelöscht');
      }
      setConfirm(null);
    } catch (err: any) {
      toast.error(err?.message || 'Löschen fehlgeschlagen');
    }
  };

  /* ---------- drag & drop ---------- */

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findTask = (id: number | string): Task | undefined =>
    tasks.find((t) => t.id === Number(id));

  const findColumnOf = (id: number | string): TaskStatus | null => {
    // Column drop target uses its TaskStatus id directly
    if (Object.values(TaskStatus).includes(id as TaskStatus)) return id as TaskStatus;
    return findTask(id)?.status ?? null;
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveTask(findTask(e.active.id) ?? null);
  };

  /** Cross-column live preview: optimistically move the dragged task into the
   * hovered column so the user sees the drop target before releasing. */
  const handleDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const activeColumn = findColumnOf(activeId);
    const overColumn = findColumnOf(overId);
    if (!activeColumn || !overColumn || activeColumn === overColumn) return;

    setTasks((prev) => {
      const activeTask = prev.find((t) => t.id === Number(activeId));
      if (!activeTask) return prev;

      // Compute target index inside overColumn
      const overItems = prev.filter((t) => t.status === overColumn).sort((a, b) => a.position - b.position);
      const overIsColumn = Object.values(TaskStatus).includes(overId as TaskStatus);
      let newIndex: number;
      if (overIsColumn) {
        newIndex = overItems.length; // dropped on empty column area => bottom
      } else {
        const idx = overItems.findIndex((t) => t.id === Number(overId));
        newIndex = idx >= 0 ? idx : overItems.length;
      }

      // Recompute provisional positions for the over column only
      const moved = { ...activeTask, status: overColumn };
      const newOverItems = [...overItems];
      newOverItems.splice(newIndex, 0, moved);
      const repositioned = newOverItems.map((t, i) => ({ ...t, position: (i + 1) * 1024 }));

      const others = prev.filter((t) => t.id !== Number(activeId) && t.status !== overColumn);
      return [...others, ...repositioned];
    });
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveTask(null);
    if (!over) return;

    const activeId = Number(active.id);
    const overId = over.id;
    const dropped = tasks.find((t) => t.id === activeId);
    if (!dropped) return;

    const targetColumn: TaskStatus =
      Object.values(TaskStatus).includes(overId as TaskStatus)
        ? (overId as TaskStatus)
        : findTask(overId)?.status ?? dropped.status;

    // Reorder within the target column to determine neighbours
    const colItems = tasks
      .filter((t) => t.status === targetColumn)
      .sort((a, b) => a.position - b.position);

    const oldIndex = colItems.findIndex((t) => t.id === activeId);
    let newIndex: number;
    const overIsColumn = Object.values(TaskStatus).includes(overId as TaskStatus);
    if (overIsColumn) {
      newIndex = colItems.length - 1; // dropped at column-level => bottom (after current items minus self if present)
      // If active not in this column yet, newIndex should be after the last existing item
      newIndex = oldIndex >= 0 ? colItems.length - 1 : colItems.length;
    } else {
      const idx = colItems.findIndex((t) => t.id === Number(overId));
      newIndex = idx >= 0 ? idx : colItems.length;
    }

    const reordered = oldIndex >= 0
      ? arrayMove(colItems, oldIndex, newIndex)
      : [...colItems.slice(0, newIndex), dropped, ...colItems.slice(newIndex)];

    const finalIndex = reordered.findIndex((t) => t.id === activeId);
    const before = finalIndex > 0 ? reordered[finalIndex - 1] : null;
    const after = finalIndex < reordered.length - 1 ? reordered[finalIndex + 1] : null;

    // Optimistic local apply
    setTasks((prev) => {
      const others = prev.filter((t) => t.status !== targetColumn && t.id !== activeId);
      const repositioned = reordered.map((t, i) => ({
        ...t,
        status: targetColumn,
        position: (i + 1) * 1024,
      }));
      return [...others, ...repositioned];
    });

    try {
      const saved = await taskService.move(activeId, {
        status: targetColumn,
        beforeId: before?.id ?? null,
        afterId: after?.id ?? null,
      });
      setTasks((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    } catch (err: any) {
      toast.error('Verschieben fehlgeschlagen, lade neu');
      await load();
    }
  };

  /* ---------- render ---------- */

  if (loading && !project) {
    return (
      <div className="p-6 lg:p-10">
        <div className="skeleton h-7 w-1/3 mb-3" />
        <div className="skeleton h-4 w-1/4 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-80 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-10">
        <p style={{ color: 'var(--text-muted)' }}>Projekt nicht gefunden.</p>
      </div>
    );
  }

  const totalCount = tasks.length;
  const doneCount = tasksByColumn[TaskStatus.DONE].length;

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      {/* Breadcrumb + Back */}
      <div className="mb-4 flex items-center gap-2 text-xs animate-fade-up">
        <Link
          to="/workspace"
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          <I.Back /> Kunden
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        {project.clientName && (
          <>
            <span style={{ color: 'var(--text-muted)' }}>{project.clientName}</span>
            <span style={{ color: 'var(--text-muted)' }}>/</span>
          </>
        )}
        <span style={{ color: 'var(--text-secondary)' }} className="font-medium">{project.name}</span>
      </div>

      {/* Project header */}
      <header className="mb-8 animate-fade-up">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1
              className="text-3xl sm:text-4xl font-extrabold tracking-tight"
              style={{ color: 'var(--text-primary)', letterSpacing: '-0.025em' }}
            >
              {project.name}
            </h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <Badge variant={statusVariant(project.status)}>{statusLabel(project.status)}</Badge>
              {!project.active && <Badge variant="red">Inaktiv</Badge>}
              {project.domain && (
                <a
                  href={toHref(project.domain)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-mono font-medium px-2 py-1 rounded-md transition-colors"
                  style={{
                    background: 'var(--badge-green-bg)',
                    color: 'var(--badge-green-text)',
                  }}
                  title={`Prod öffnen · ${prettyDomain(project.domain)}`}
                >
                  <I.Globe />
                  <span className="truncate max-w-[28ch]">{prettyDomain(project.domain)}</span>
                  <I.External />
                </a>
              )}
              <span className="text-xs nums-tabular" style={{ color: 'var(--text-muted)' }}>
                {doneCount}/{totalCount} erledigt
              </span>
            </div>
            {project.description && (
              <p className="mt-3 text-sm max-w-2xl leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {project.description}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Repositories */}
      <section className="mb-8 animate-fade-up">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="text-[10px] uppercase font-bold tracking-[0.2em]"
            style={{ color: 'var(--text-muted)' }}
          >
            Repositories
          </span>
          <span
            className="text-[11px] nums-tabular px-1.5 py-0.5 rounded font-semibold"
            style={{ background: 'var(--surface-bg)', color: 'var(--text-muted)', border: '1px solid var(--surface-border)' }}
          >
            {repos.length}
          </span>
          <span className="flex-1 h-px" style={{ background: 'var(--surface-border)' }} />
          <button
            onClick={() => setEditor({ kind: 'repo', mode: 'create', data: { ...emptyRepo } })}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--table-header-bg)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <I.Plus /> Repository
          </button>
        </div>

        {repos.length === 0 ? (
          <button
            onClick={() => setEditor({ kind: 'repo', mode: 'create', data: { ...emptyRepo } })}
            className="w-full py-5 text-xs rounded-lg flex items-center justify-center gap-2 transition opacity-70 hover:opacity-100"
            style={{
              color: 'var(--text-muted)',
              background: 'var(--table-header-bg)',
              border: '1px dashed var(--surface-border)',
            }}
          >
            <I.Plus /> Erstes Repository anlegen
          </button>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {repos.map((r) => (
              <RepoTile
                key={r.id}
                repo={r}
                onEdit={() => setEditor({
                  kind: 'repo', mode: 'edit', id: r.id,
                  data: {
                    name: r.name,
                    gitUrl: r.gitUrl,
                    defaultBranch: r.defaultBranch,
                    description: r.description ?? '',
                    active: r.active,
                  },
                })}
                onDelete={() => setConfirm({ kind: 'repo', id: r.id, name: r.name })}
              />
            ))}
          </div>
        )}
      </section>

      {/* Kanban */}
      <div className="flex items-center gap-3 mb-4 animate-fade-up">
        <span
          className="text-[10px] uppercase font-bold tracking-[0.2em]"
          style={{ color: 'var(--text-muted)' }}
        >
          Aufgaben
        </span>
        <span
          className="text-[11px] nums-tabular px-1.5 py-0.5 rounded font-semibold"
          style={{ background: 'var(--surface-bg)', color: 'var(--text-muted)', border: '1px solid var(--surface-border)' }}
        >
          {tasks.length}
        </span>
        <span className="flex-1 h-px" style={{ background: 'var(--surface-border)' }} />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
          {COLUMNS.map((col) => (
            <Column
              key={col.id}
              column={col}
              tasks={tasksByColumn[col.id]}
              onAdd={() => setEditor({ kind: 'task', mode: 'create', data: emptyTask(col.id) })}
              onEdit={(task) => setEditor({
                kind: 'task', mode: 'edit', id: task.id,
                data: {
                  title: task.title,
                  description: task.description ?? '',
                  status: task.status,
                  priority: task.priority,
                  tags: task.tags ?? [],
                },
              })}
              onDelete={(task) => setConfirm({ kind: 'task', id: task.id, title: task.title })}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeTask ? <TaskCard task={activeTask} isDragging /> : null}
        </DragOverlay>
      </DndContext>

      {/* Editor modal (task or repo) */}
      {editor && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="task-modal-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setEditor(null); }}
          innerRef={modalRef}
        >
          <div className="rounded-xl p-6 w-full max-w-md" style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="task-modal-title" className="text-lg font-semibold mb-5 tracking-tight">
              {editorTitle(editor)}
            </h2>
            <form onSubmit={submit}>
              {editor.kind === 'task' ? (
                <TaskFields
                  data={editor.data}
                  onChange={(patch) => setEditor({ ...editor, data: { ...editor.data, ...patch } } as Editor)}
                />
              ) : (
                <RepoFields
                  data={editor.data} mode={editor.mode}
                  onChange={(patch) => setEditor({ ...editor, data: { ...editor.data, ...patch } } as Editor)}
                />
              )}

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button" onClick={() => setEditor(null)}
                  className="px-4 py-2 rounded-lg text-sm"
                  style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn-primary px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-semibold"
                >
                  {editor.mode === 'edit' ? 'Speichern' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* Delete confirm */}
      {confirm && (
        <ModalOverlay
          role="alertdialog" ariaLabelledBy="task-del-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirm(null); }}
        >
          <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: 'var(--surface-bg)' }}>
            <h2 id="task-del-title" className="text-base font-semibold mb-2 tracking-tight" style={{ color: 'var(--text-primary)' }}>
              {confirm.kind === 'task' ? 'Aufgabe löschen' : 'Repository löschen'}
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Möchtest du <strong>{confirm.kind === 'task' ? confirm.title : confirm.name}</strong> wirklich löschen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)} autoFocus
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
              >
                Abbrechen
              </button>
              <button
                onClick={doDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
              >
                Löschen
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

const editorTitle = (e: Editor): string => {
  if (e.kind === 'task') return e.mode === 'edit' ? 'Aufgabe bearbeiten' : 'Neue Aufgabe';
  return e.mode === 'edit' ? 'Repository bearbeiten' : 'Neues Repository';
};

/* ============================================================
   TaskFields
   ============================================================ */

const TaskFields: React.FC<{
  data: TaskForm;
  onChange: (patch: Partial<TaskForm>) => void;
}> = ({ data, onChange }) => {
  const [tagDraft, setTagDraft] = useState('');

  const addTag = (raw: string) => {
    const next = raw.trim();
    if (!next || data.tags.includes(next)) return;
    onChange({ tags: [...data.tags, next] });
  };

  const commitDraft = () => {
    // Split on commas so pasting "a, b" adds both.
    tagDraft.split(',').forEach(addTag);
    setTagDraft('');
  };

  const removeTag = (tag: string) =>
    onChange({ tags: data.tags.filter((t) => t !== tag) });

  return (
    <>
      <Field label="Titel *">
        <input
          type="text" required value={data.title}
          onChange={(e) => onChange({ title: e.target.value })}
          className="w-full p-2.5 border rounded-lg input-themed text-sm"
        />
      </Field>
      <Field label="Beschreibung">
        <textarea
          rows={4} value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          className="w-full p-2.5 border rounded-lg input-themed text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status">
          <select
            value={data.status}
            onChange={(e) => onChange({ status: e.target.value as TaskStatus })}
            className="w-full p-2.5 border rounded-lg input-themed text-sm"
          >
            {COLUMNS.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Priorität">
          <select
            value={data.priority}
            onChange={(e) => onChange({ priority: e.target.value as TaskPriority })}
            className="w-full p-2.5 border rounded-lg input-themed text-sm"
          >
            {Object.values(TaskPriority).map((p) => (
              <option key={p} value={p}>{priorityLabel(p)}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Tags">
        {data.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {data.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded"
                style={{ background: 'var(--badge-purple-bg)', color: 'var(--badge-purple-text)' }}
              >
                #{tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="leading-none text-sm hover:opacity-70"
                  aria-label={`Tag ${tag} entfernen`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          type="text"
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitDraft(); }
          }}
          onBlur={commitDraft}
          placeholder="z. B. agent, frontend – Enter zum Hinzufügen"
          className="w-full p-2.5 border rounded-lg input-themed text-sm"
        />
      </Field>
    </>
  );
};

/* ============================================================
   RepoFields
   ============================================================ */

const RepoFields: React.FC<{
  data: RepoForm;
  mode: 'create' | 'edit';
  onChange: (patch: Partial<RepoForm>) => void;
}> = ({ data, mode, onChange }) => (
  <>
    <Field label="Name *">
      <input
        type="text" required value={data.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className="w-full p-2.5 border rounded-lg input-themed text-sm"
      />
    </Field>
    <Field label="Git URL *">
      <input
        type="text" required value={data.gitUrl}
        placeholder="git@github.com:org/repo.git"
        onChange={(e) => onChange({ gitUrl: e.target.value })}
        className="w-full p-2.5 border rounded-lg input-themed text-sm font-mono"
      />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Default-Branch">
        <input
          type="text" value={data.defaultBranch}
          onChange={(e) => onChange({ defaultBranch: e.target.value })}
          className="w-full p-2.5 border rounded-lg input-themed text-sm font-mono"
        />
      </Field>
      {mode === 'edit' && (
        <Field label="Aktiv">
          <label className="flex items-center text-sm h-[42px]" style={{ color: 'var(--text-primary)' }}>
            <input
              type="checkbox" checked={data.active}
              onChange={(e) => onChange({ active: e.target.checked })}
              className="mr-2"
            />
            Aktiv
          </label>
        </Field>
      )}
    </div>
    <Field label="Beschreibung">
      <textarea
        rows={2} value={data.description}
        onChange={(e) => onChange({ description: e.target.value })}
        className="w-full p-2.5 border rounded-lg input-themed text-sm"
      />
    </Field>
  </>
);

/* ============================================================
   RepoTile (read-only display + actions)
   ============================================================ */

const RepoTile: React.FC<{
  repo: Repository;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ repo, onEdit, onDelete }) => {
  const displayUrl = repo.gitUrl
    .replace(/^https?:\/\//, '')
    .replace(/^git@/, '')
    .replace(/\.git$/, '');

  return (
    <div
      className="group relative rounded-xl p-4 transition-colors"
      style={{
        background: 'var(--surface-bg)',
        border: '1px solid var(--surface-border)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--focus-color)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--surface-border)'; }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm tracking-tight truncate" style={{ color: 'var(--text-primary)' }}>
              {repo.name}
            </span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--table-header-bg)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--surface-border)',
              }}
              title={`Default-Branch: ${repo.defaultBranch}`}
            >
              <I.Branch /> {repo.defaultBranch}
            </span>
            {!repo.active && <Badge variant="red">Inaktiv</Badge>}
          </div>
        </div>

        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            className="h-7 w-7 rounded inline-flex items-center justify-center"
            style={{ color: 'var(--text-muted)' }}
            title="Bearbeiten"
            aria-label="Repository bearbeiten"
          >
            <I.Edit />
          </button>
          <button
            onClick={onDelete}
            className="h-7 w-7 rounded inline-flex items-center justify-center"
            style={{ color: 'var(--badge-red-text)' }}
            title="Löschen"
            aria-label="Repository löschen"
          >
            <I.Trash />
          </button>
        </div>
      </div>

      <a
        href={repo.gitUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs font-mono hover:opacity-80 max-w-full"
        style={{ color: 'var(--text-secondary)' }}
        title={repo.gitUrl}
      >
        <I.Link />
        <span className="truncate">{displayUrl}</span>
      </a>

      {repo.description && (
        <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          {repo.description}
        </p>
      )}
    </div>
  );
};

/* ============================================================
   Column
   ============================================================ */

const Column: React.FC<{
  column: { id: TaskStatus; label: string; hint: string };
  tasks: Task[];
  onAdd: () => void;
  onEdit: (t: Task) => void;
  onDelete: (t: Task) => void;
}> = ({ column, tasks, onAdd, onEdit, onDelete }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const ids = useMemo(() => tasks.map((t) => t.id), [tasks]);

  return (
    <section
      ref={setNodeRef}
      className="rounded-xl flex flex-col"
      style={{
        background: isOver ? 'var(--table-row-hover)' : 'var(--table-header-bg)',
        border: '1px solid var(--surface-border)',
        minHeight: 320,
        transition: 'background-color .12s ease',
      }}
    >
      {/* Header */}
      <header className="px-4 pt-3.5 pb-3 flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: columnDot(column.id) }}
        />
        <h3
          className="text-[11px] font-bold uppercase tracking-[0.18em]"
          style={{ color: 'var(--text-secondary)' }}
        >
          {column.label}
        </h3>
        <span
          className="text-[11px] nums-tabular px-1.5 py-0.5 rounded font-semibold"
          style={{ background: 'var(--surface-bg)', color: 'var(--text-muted)', border: '1px solid var(--surface-border)' }}
        >
          {tasks.length}
        </span>
        <button
          onClick={onAdd}
          className="ml-auto inline-flex items-center justify-center h-6 w-6 rounded transition-colors"
          style={{ color: 'var(--text-muted)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--surface-bg)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
          title="Aufgabe hinzufügen"
          aria-label="Aufgabe hinzufügen"
        >
          <I.Plus />
        </button>
      </header>

      {/* Tasks */}
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="px-3 pb-3 flex-1 flex flex-col gap-2.5">
          {tasks.length === 0 ? (
            <button
              onClick={onAdd}
              className="flex-1 min-h-[120px] rounded-lg text-xs inline-flex items-center justify-center gap-1.5 transition opacity-60 hover:opacity-100"
              style={{
                color: 'var(--text-muted)',
                background: 'transparent',
                border: '1px dashed var(--surface-border)',
              }}
            >
              <I.Plus /> Erste Aufgabe
            </button>
          ) : (
            tasks.map((task) => (
              <SortableTask
                key={task.id}
                task={task}
                onEdit={() => onEdit(task)}
                onDelete={() => onDelete(task)}
              />
            ))
          )}
        </div>
      </SortableContext>
    </section>
  );
};

const columnDot = (s: TaskStatus): string => {
  switch (s) {
    case TaskStatus.TODO:        return 'var(--text-muted)';
    case TaskStatus.IN_PROGRESS: return 'var(--card-accent-amber)';
    case TaskStatus.DONE:        return 'var(--card-accent-emerald)';
  }
};

/* ============================================================
   Sortable task card
   ============================================================ */

const SortableTask: React.FC<{
  task: Task;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ task, onEdit, onDelete }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: task.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={task} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
};

/* ============================================================
   Task card (pure)
   ============================================================ */

const TaskCard: React.FC<{
  task: Task;
  onEdit?: () => void;
  onDelete?: () => void;
  isDragging?: boolean;
}> = ({ task, onEdit, onDelete, isDragging }) => {
  return (
    <article
      className={`group relative rounded-lg p-3 transition-shadow ${isDragging ? 'shadow-2xl scale-[1.02]' : ''}`}
      style={{
        background: 'var(--surface-bg)',
        border: '1px solid var(--surface-border)',
        boxShadow: isDragging
          ? '0 18px 40px -10px rgba(0,0,0,0.35), 0 2px 6px -1px rgba(0,0,0,0.15)'
          : 'var(--surface-shadow)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      {/* Priority accent strip */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-2 bottom-2 w-1 rounded-r"
        style={{ background: priorityAccent(task.priority) }}
      />

      <div className="pl-2">
        <div className="flex items-start gap-2 mb-1">
          <h4 className="text-sm font-semibold tracking-tight flex-1 leading-snug" style={{ color: 'var(--text-primary)' }}>
            {task.title}
          </h4>
          <span
            className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
            style={priorityChipStyle(task.priority)}
          >
            {priorityLabel(task.priority)}
          </span>
        </div>

        {task.description && (
          <p
            className="text-xs leading-relaxed clamp-3 mt-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            {task.description}
          </p>
        )}

        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {task.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ background: 'var(--badge-purple-bg)', color: 'var(--badge-purple-text)' }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {(onEdit || onDelete) && (
          <div className="flex items-center justify-between mt-2.5 pt-2" style={{ borderTop: '1px solid var(--surface-border)' }}>
            <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              <I.Drag /> ziehen
            </span>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onEdit && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  className="h-6 w-6 rounded inline-flex items-center justify-center"
                  style={{ color: 'var(--text-muted)' }}
                  title="Bearbeiten"
                >
                  <I.Edit />
                </button>
              )}
              {onDelete && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="h-6 w-6 rounded inline-flex items-center justify-center"
                  style={{ color: 'var(--badge-red-text)' }}
                  title="Löschen"
                >
                  <I.Trash />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

/* ============================================================
   Field helper
   ============================================================ */

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
      {label}
    </label>
    {children}
  </div>
);

export default ProjectDetail;
